/**
 * Standalone-boot session bootstrap.
 *
 * When a user logs in (or switches profile) anywhere, the shared cy-sso
 * `Authorization` + `refresh-token` cookies are written on the parent domain
 * (`.codeyoung.com`), along with a `cy_auth_sync` flag. On boot we read those
 * cookies and rebuild `localStorage.sandbox_user` via `saveSession`.
 *
 * Source-of-truth rule: the shared cookies win. If a session already exists but
 * its token differs from the cookie (e.g. the user switched profile in the
 * portal, which re-wrote the cookie + bumped `cy_auth_sync`), we adopt the
 * cookie token. The legacy `isRedirectedFromPortal` flag is folded into the
 * generic `cy_auth_sync`.
 *
 * Runs only in standalone mode — embedded mode gets its session seeded by the
 * portal's SandboxRenderer.
 */

import jwtDecode from "jwt-decode";
import { getCookie, deleteCookieAllDomains, isLoggedOut } from "./cookieUtils";
import { decrypt } from "./others";
import { saveSession } from "./auth";
import { isNewerToken, tokenRole } from "../../Services/tokenManager";

// A token is "cy-sso" once it carries accessTokenSecretId (added post-migration).
const isCySsoToken = (token) => {
  try {
    return !!jwtDecode(token)?.accessTokenSecretId;
  } catch {
    return false;
  }
};

// The user/student id a token belongs to (used to tell a profile SWITCH —
// different user — apart from a same-user token migration).
const tokenUserId = (token) => {
  try {
    const d = jwtDecode(token) || {};
    return d.userId || d.studentId || d.sub || null;
  } catch {
    return null;
  }
};

export const bootstrapSessionFromCookie = () => {
  // A cross-app logout signal outranks any lingering auth cookie — never
  // auto-login a session the user just logged out of. (A fresh login clears the
  // flag via saveSession, so this only blocks stale post-logout state.)
  if (isLoggedOut()) return false;

  const accessToken = getCookie("Authorization");
  const refreshToken = getCookie("refresh-token");
  if (!accessToken || !refreshToken) return false;

  // Only STUDENT sessions participate in cross-app auto-login (symmetric with
  // the student-only cookie write in saveSession). A non-student cookie is
  // handoff transport for the /sso-login route (Epicenter → mentor one-click),
  // not a sync source: mentor refreshes never re-write the shared cookies, so
  // a lingering mentor cookie is stale by definition — adopting it here (or
  // auto-logging-in from it on a fresh boot) hands out rotated-away tokens.
  if (tokenRole(accessToken) !== "student") return false;

  const stored = localStorage.getItem("sandbox_user");
  let localData = {};
  let localToken = null;
  if (stored) {
    try {
      localData = decrypt(stored) || {};
      localToken = localData.ACCESS_TOKEN || localData.Authorization;
    } catch (_) {
      localData = {};
      localToken = null;
    }
  }

  // Adopt the shared cookie token when there's no local session (fresh-tab
  // auto-login) OR a local session exists but the cookie carries a DIFFERENT
  // token (a login / profile-switch happened in the other app / another tab).
  // The shared cookies are the source of truth. We deliberately do NOT gate
  // this on a sync flag: on localhost the cookies are shared across ports, so
  // whichever app boots first would consume the flag and the other app would
  // then never adopt — token comparison is the reliable cross-app signal.
  let shouldAdopt = !stored || localToken !== accessToken;

  // Guard ONLY against same-user token-migration churn: a one-time migration
  // upgrades localStorage to a cy-sso token but leaves the cookie on the legacy
  // token, which would otherwise make boot re-adopt the stale legacy cookie on
  // every reload. We must NOT block a profile SWITCH (a different user) — that
  // is exactly the cross-app sync we want — so only skip when it's the SAME
  // user being downgraded cy-sso → legacy.
  if (shouldAdopt && stored) {
    const sameUser =
      tokenUserId(localToken) &&
      tokenUserId(localToken) === tokenUserId(accessToken);
    if (sameUser && isCySsoToken(localToken) && !isCySsoToken(accessToken)) {
      shouldAdopt = false;
    }
    // Freshness guard: over an EXISTING session, adopt only a cookie token
    // issued STRICTLY LATER than the local one. Every legitimate sync source
    // (login, profile switch, refresh) mints a brand-new token, so a not-newer
    // cookie is stale — adopting it would downgrade a live session to
    // rotated-away tokens and log the user out on the next request.
    if (shouldAdopt && !isNewerToken(accessToken, localToken)) {
      shouldAdopt = false;
    }
  }

  if (!shouldAdopt) {
    // Already in sync (or guarded) — consume any stale handoff flags.
    deleteCookieAllDomains("cy_auth_sync");
    deleteCookieAllDomains("isRedirectedFromPortal");
    return false;
  }

  // Adopt the cookie token. saveSession rebuilds sandbox_user from the JWT and
  // syncs Axios. For the SAME user (e.g. a token refresh propagated via the
  // cookie) preserve the existing profile fields — notably `isPaid` — so we
  // don't momentarily reset a paid user to "unpaid" and flash the guest home.
  // For a DIFFERENT user (profile switch) start fresh; the home page resolves
  // the new user's paid status (and gates on it via a loader).
  const hadSession = !!stored;
  const sameUser =
    hadSession &&
    tokenUserId(localToken) &&
    tokenUserId(localToken) === tokenUserId(accessToken);
  saveSession({
    accessToken,
    refreshToken,
    userData: sameUser ? localData : {},
  });

  // Consume the handoff/sync flags so subsequent boots don't re-adopt.
  deleteCookieAllDomains("cy_auth_sync");
  deleteCookieAllDomains("isRedirectedFromPortal");

  // Only on a fresh handoff (no prior session) do we move off the public
  // login/landing route to /home — don't yank a deep-linked, already-open app.
  if (!hadSession) {
    const hash = window.location.hash || "";
    if (hash === "" || hash === "#/" || hash.startsWith("#/login")) {
      window.location.hash = "/home";
    }
  }
  return true;
};

export default bootstrapSessionFromCookie;
